/**
 * Clinic portal treatment report API.
 * Pure functions for treatment report list operations. No React or localStorage.
 */

import { apiClient } from '@shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';

export interface TreatmentReportListItem {
  id: number;
  patient_id: number;
  serial_number: number;
  treatment_report_date: string | null;
  treatment_yearmonth: number;
  massager_id: number | null;
  clinic_location_id: number | null;
  clinic_location?: ClinicLocationOption | null;
  massager_name: string | null;
  hospital_id: number | null;
  hospital_name: string | null;
  doctor_id: number | null;
  doctor_name: string | null;
  contents_frequency: string | null;
  status_course: string | null;
  remarks: string | null;
  non_print_cols: string | null;
  postal_code1: string | null;
  postal_code2: string | null;
  address: string | null;
  address3: string | null;
  telephone: string | null;
  fax: string | null;
  email: string | null;
  is_treatment_report_default: boolean;
  second_flag: number | null;
  treatment_monthly_avg: number | null;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export interface TreatmentReportListData {
  patient: any;
  treatment_reports: TreatmentReportListItem[];
}

export type MassagerFilterOptions = {
  treatmentYearmonth?: number;
  filterByHeaderServiceType?: boolean;
  excludeExpiredCertificate?: boolean;
};

export type TreatmentReportListQueryParams = {
  sort_type?: string;
  sort_dir?: string;
  treatment_yearmonth?: number;
  service_type?: string;
};

export interface MassagerOption {
  id: number;
  serial_number: number | null;
  name: string;
  email: string | null;
}

export interface ClinicLocationOption {
  id: number;
  location_name: string;
  postal_code1: string | null;
  postal_code2: string | null;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  telephone: string | null;
  fax: string | null;
}

export interface TreatmentReportDetailData {
  treatment_report: TreatmentReportListItem;
  massagers: MassagerOption[];
}

export interface TreatmentReportUpdatePayload {
  treatment_yearmonth: number;
  treatment_report_date: string;
  contents_frequency?: string | null;
  massager_id: number;
  clinic_location_id?: number | null;
  is_treatment_report_default?: boolean;
  second_flag?: boolean;
  hospital_id?: number | null;
  doctor_id?: number | null;
  treatment_monthly_avg?: number | null;
  status_course?: string | null;
  remarks?: string | null;
  address3?: string | null;
  postal_code1?: string | null;
  postal_code2?: string | null;
  address?: string | null;
  telephone?: string | null;
  fax?: string | null;
  email?: string | null;
  non_print_cols?: string[];
}

function toParams(params: TreatmentReportListQueryParams | undefined): Record<string, string | number> {
  const result: Record<string, string | number> = {};
  if (!params) return result;
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
  });
  return result;
}

/**
 * Get treatment report list for a patient
 */
export async function getTreatmentReportsList(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  serviceType: string,
  params?: TreatmentReportListQueryParams
): Promise<ApiResponse<TreatmentReportListData>> {
  const response = await apiClient.get<ApiResponse<TreatmentReportListData>>(
    clinicApiPath(siteType, clinicNumber, `/treatment-reports/${patientId}/list`),
    {
      params: toParams(params),
      headers: {
        'X-Service-Type': serviceType,
      },
    }
  );
  return response.data;
}

/**
 * Get treatment report detail for edit form
 */
export async function getTreatmentReportDetail(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: string,
  serviceType: string,
  options?: MassagerFilterOptions
): Promise<ApiResponse<TreatmentReportDetailData>> {
  const params: Record<string, string | number | boolean> = {
    service_type: serviceType,
    filter_by_header_service_type: options?.filterByHeaderServiceType ?? true,
    exclude_expired_certificate: options?.excludeExpiredCertificate ?? true,
  };
  if (options?.treatmentYearmonth) {
    params.range_type = 'month';
    params.range_value = options.treatmentYearmonth;
    params.treatment_yearmonth = options.treatmentYearmonth;
  }

  const response = await apiClient.get<ApiResponse<TreatmentReportDetailData>>(
    clinicApiPath(siteType, clinicNumber, `/treatment-reports/${patientId}/${id}`),
    {
      params: toParams(params),
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Get init data for create form (massagers list)
 */
export async function getTreatmentReportInit(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  serviceType: string,
  options?: MassagerFilterOptions
): Promise<ApiResponse<{ massagers: MassagerOption[]; active_consent_content: string | null }>> {
  const params: Record<string, string | number | boolean> = {
    service_type: serviceType,
    filter_by_header_service_type: options?.filterByHeaderServiceType ?? true,
    exclude_expired_certificate: options?.excludeExpiredCertificate ?? true,
  };
  if (options?.treatmentYearmonth) {
    params.range_type = 'month';
    params.range_value = options.treatmentYearmonth;
    params.treatment_yearmonth = options.treatmentYearmonth;
  }

  const response = await apiClient.get<ApiResponse<{ massagers: MassagerOption[]; active_consent_content: string | null }>>(
    clinicApiPath(siteType, clinicNumber, `/treatment-reports/${patientId}/init`),
    {
      params: toParams(params),
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Create new treatment report
 */
export async function createTreatmentReport(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  serviceType: string,
  data: TreatmentReportUpdatePayload
): Promise<ApiResponse<TreatmentReportListItem>> {
  const response = await apiClient.post<ApiResponse<TreatmentReportListItem>>(
    clinicApiPath(siteType, clinicNumber, `/treatment-reports/${patientId}`),
    data,
    {
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Update treatment report
 */
export async function updateTreatmentReport(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: string,
  serviceType: string,
  data: TreatmentReportUpdatePayload
): Promise<ApiResponse<TreatmentReportListItem>> {
  const response = await apiClient.put<ApiResponse<TreatmentReportListItem>>(
    clinicApiPath(siteType, clinicNumber, `/treatment-reports/${patientId}/${id}`),
    data,
    {
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Fetch treatment report PDF as blob
 */
export async function fetchTreatmentReportPdf(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: string,
  serviceType: string
): Promise<Blob> {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, `/treatment-reports/${patientId}/${id}/pdf`),
    {
      responseType: 'blob',
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Delete treatment report
 */
export async function deleteTreatmentReport(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: string,
  serviceType: string
): Promise<ApiResponse<unknown>> {
  const response = await apiClient.delete<ApiResponse<unknown>>(
    clinicApiPath(siteType, clinicNumber, `/treatment-reports/${patientId}/${id}`),
    {
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Calculate treatment frequency average (再取得)
 */
export async function searchTreatmentCount(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  serviceType: string,
  treatmentReportDate: string,
  secondFlag: boolean = false,
  treatmentYearmonth?: number | string | null
): Promise<ApiResponse<{ count: number; active_consent_content: string | null }>> {
  const response = await apiClient.post<ApiResponse<{ count: number; active_consent_content: string | null }>>(
    clinicApiPath(siteType, clinicNumber, `/treatment-reports/${patientId}/search-count`),
    {
      treatment_report_date: treatmentReportDate,
      second_flag: secondFlag,
      ...(treatmentYearmonth ? { treatment_yearmonth: treatmentYearmonth } : {}),
    },
    {
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Get clinic locations for a massager
 */
export async function getMassagerLocations(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  serviceType: string,
  massagerId: number
): Promise<ApiResponse<{ locations: ClinicLocationOption[] }>> {
  const response = await apiClient.get<ApiResponse<{ locations: ClinicLocationOption[] }>>(
    clinicApiPath(siteType, clinicNumber, `/treatment-reports/${patientId}/massager-locations/${massagerId}`),
    {
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}
